/**
 * Canonical public origin of the staff web app, for every link that leaves the
 * running process: email CTAs and footers, digests, signing links and
 * family-portal-to-staff deep links.
 *
 * A hosted runtime never answers with a loopback or plain-http origin. From
 * 11 Sep 2026 production's configured app URL resolved to
 * `http://localhost:3000`, so every app link in production email (bounce
 * alerts, system-health alerts, Xero alerts, compliance roundups and every
 * footer) pointed at the recipient's own machine. A value that cannot be a
 * public link is skipped, then the next candidate is tried, then the
 * production origin is used.
 *
 * Outside a hosted runtime (a developer machine) loopback is accepted so local
 * links keep working; `getOutboundCommunicationSuppressionReason()` stops such a
 * process from delivering those links to real recipients.
 */

export const PRODUCTION_APP_ORIGIN = 'https://app.statura.care';

export const DEFAULT_APP_ORIGIN_ENV_KEYS = [
  'APP_URL',
  'NEXT_PUBLIC_APP_URL',
  'NEXT_PUBLIC_SITE_URL',
] as const;

type EnvironmentLike = Record<string, string | undefined>;

const LOOPBACK_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '::1']);

let warnedKeys: Set<string> | null = null;

function parseUrl(value: string | null | undefined): URL | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

function hostnameOf(url: URL): string {
  return url.hostname.toLowerCase().replace(/^\[|\]$/g, '');
}

export function isLoopbackOrigin(value: string | null | undefined): boolean {
  const url = parseUrl(value);
  if (!url) return false;
  const hostname = hostnameOf(url);
  return LOOPBACK_HOSTNAMES.has(hostname) || hostname.endsWith('.localhost');
}

/** Vercel sets `VERCEL=1` (and `VERCEL_ENV`) for every build and function it runs. */
export function isHostedRuntime(env: EnvironmentLike = process.env): boolean {
  return Boolean(env.VERCEL?.trim() || env.VERCEL_ENV?.trim());
}

function warnRejected(key: string): void {
  warnedKeys ??= new Set();
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn('[app-origin] configured app URL cannot be a public link; ignoring it', { key });
}

export function resolveAppOrigin(
  env: EnvironmentLike = process.env,
  keys: readonly string[] = DEFAULT_APP_ORIGIN_ENV_KEYS,
): string {
  const hosted = isHostedRuntime(env);
  for (const key of keys) {
    const url = parseUrl(env[key]);
    if (!url) continue;
    if (hosted && (url.protocol !== 'https:' || isLoopbackOrigin(url.origin))) {
      warnRejected(key);
      continue;
    }
    return url.origin;
  }
  return PRODUCTION_APP_ORIGIN;
}

/** Absolute app URL for a path; an already-absolute http(s) href is returned unchanged. */
export function appUrl(path: string, env: EnvironmentLike = process.env): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${resolveAppOrigin(env)}${path.startsWith('/') ? '' : '/'}${path}`;
}
